import React from "react";
import { motion } from "framer-motion";
import { ArrowRight, Phone, MessageCircle } from "lucide-react";
import { COMPANY, buildWhatsAppLink } from "@/lib/constants";

export default function Hero() {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative bg-asphalt text-bone overflow-hidden pt-24 lg:pt-28"
    >
      {/* Background image */}
      <div className="absolute inset-0 z-0">
        <img
          src="/assets/ops3.jpg"
          alt="Camión Royal Eco en planta de gestión de residuos industriales"
          className="w-full h-full object-cover opacity-25"
          loading="eager"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-asphalt via-asphalt/90 to-asphalt/40" />
        <div className="absolute inset-0 bg-gradient-to-t from-asphalt via-transparent to-transparent" />
      </div>

      <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-10 pt-20 pb-24 md:pt-24 md:pb-32">
        {/* Top meta strip — clean and minimal */}
        <div className="flex flex-wrap items-center gap-x-8 gap-y-3 mb-16">
          <div className="flex items-center gap-2.5">
            <span className="w-1.5 h-1.5 rounded-full bg-lime-400" />
            <span className="tag text-lime-400">Operativo 24/7</span>
          </div>
          <span className="tag text-bone/50">
            Est. 2014 — Buenos Aires
          </span>
          <span className="tag text-bone/50 hidden md:inline">
            Habilitado · Min. Ambiente PBA
          </span>
        </div>

        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          {/* Left: refined headline */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-7"
          >
            <h1
              data-testid="hero-headline"
              className="font-display text-[54px] sm:text-[78px] md:text-[96px] lg:text-[112px] leading-[0.92] text-bone"
            >
              Gestión ambiental
              <br />
              <span className="text-lime-400">responsable</span>
            </h1>

            <div className="mt-8 max-w-xl">
              <p className="text-bone/80 text-lg leading-relaxed">
                Tratamiento de residuos especiales y peligrosos, transporte y
                disposición final de residuos industriales y domiciliarios en
                Buenos Aires. Operamos con
                <span className="text-lime-400"> trazabilidad total</span>,
                manifiestos electrónicos y respaldo técnico-legal.
              </p>
            </div>

            <div className="mt-12 flex flex-wrap items-center gap-x-8 gap-y-4">
              <a
                href={buildWhatsAppLink()}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-whatsapp-cta"
                className="group inline-flex items-center gap-3 bg-lime-400 hover:bg-lime-500 text-asphalt px-7 py-4 text-[12px] uppercase tracking-widest2 font-semibold transition-colors"
              >
                Solicitar cotización
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </a>
              <a
                href="#servicios"
                data-testid="hero-services-link"
                className="inline-flex items-center gap-2 text-bone/90 hover:text-lime-400 text-[12px] uppercase tracking-widest2 font-semibold transition-colors"
              >
                Ver servicios
                <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          </motion.div>

          {/* Right: minimal spec card */}
          <motion.aside
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="lg:col-span-5 lg:pt-4"
          >
            <div className="border-l border-bone/20 pl-8 space-y-7">
              <div className="tag text-lime-400">Información operativa</div>
              <div className="space-y-5">
                {[
                  ["Trayectoria", "+10 años"],
                  ["Cobertura", "PBA / CABA"],
                  ["Habilitaciones", "Min. Ambiente"],
                  ["Documentación", "Manifiesto + CDF"],
                ].map(([k, v]) => (
                  <div
                    key={k}
                    className="flex items-baseline justify-between gap-6 pb-4 border-b border-bone/10 last:border-0 last:pb-0"
                  >
                    <span className="text-sm text-bone/60">{k}</span>
                    <span className="font-display text-lg text-bone">{v}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-10 pl-8 space-y-4">
              <a
                href={`tel:${COMPANY.officeTel}`}
                data-testid="hero-office-phone"
                className="flex items-center gap-3 text-bone hover:text-lime-400 transition-colors group"
              >
                <Phone className="w-4 h-4 text-bone/50 group-hover:text-lime-400" />
                <div className="flex-1">
                  <div className="text-[10px] uppercase tracking-widest2 text-bone/50">Oficina</div>
                  <div className="text-base">{COMPANY.officeDisplay}</div>
                </div>
              </a>
              <a
                href={buildWhatsAppLink()}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-whatsapp-phone"
                className="flex items-center gap-3 text-bone hover:text-lime-400 transition-colors group"
              >
                <MessageCircle className="w-4 h-4 text-whatsapp" />
                <div className="flex-1">
                  <div className="text-[10px] uppercase tracking-widest2 text-bone/50">WhatsApp</div>
                  <div className="text-base">{COMPANY.whatsappDisplay}</div>
                </div>
              </a>
            </div>
          </motion.aside>
        </div>
      </div>
    </section>
  );
}
